/**
 * QuestionnaireBuilderDialog — crea o modifica un questionario di piattaforma.
 *
 * Il motore è GENERICO: domande, opzioni, schermate, email di conferma e traduzioni sono DATI
 * (forme in `shared/questionari`); qui non c'è niente che sappia di che cosa parla il
 * questionario. Il costruttore è diviso in schede, ognuna in un file di `costruttore/`.
 *
 * Prima di salvare si rifanno i controlli di `problemiDefinizione` (gli stessi del server): con
 * un problema non si salva, e si mostra quale. Se il questionario ha già risposte, i codici di
 * domande e opzioni esistenti sono bloccati (le statistiche contano i codici).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, Save as SaveIcon } from '@mui/icons-material';
import { problemiDefinizione } from '@shared/questionari';
import { tokens } from '../../theme';
import { contaRisposteQuestionario, saveQuestionnaire } from '../../services/platformCrmService';
import type { PlatformQuestionnaire } from '../../types/platformCrm';
import { NOMI_LINGUA } from './labels';
import { CampoTesto } from './costruttore/CampoTesto';
import { EditorDomanda } from './costruttore/EditorDomanda';
import { SchedaEmail } from './costruttore/SchedaEmail';
import { SchedaSchermate } from './costruttore/SchedaSchermate';
import { SchedaTraduzioni } from './costruttore/SchedaTraduzioni';
import {
  applicaDomandaCambiata, bozzaDaQuestionario, codiciDelQuestionario, NESSUN_CODICE_BLOCCATO,
  nuovaDomanda, questionarioDaBozza, type Bozza, type CodiciBloccati,
} from './costruttore/bozza';

type Scheda = 'generale' | 'domande' | 'schermate' | 'email' | 'traduzioni';

interface Props {
  open: boolean;
  /** Questionario da modificare; null = creazione nuova. */
  questionnaire: PlatformQuestionnaire | null;
  onClose: () => void;
  /** Chiamata dopo il salvataggio riuscito (il padre ricarica). */
  onSaved: () => void;
}

export const QuestionnaireBuilderDialog: React.FC<Props> = ({ open, questionnaire, onClose, onSaved }) => {
  const [bozza, setBozza] = useState<Bozza>(() => bozzaDaQuestionario(null));
  const [scheda, setScheda] = useState<Scheda>('generale');
  const [bloccati, setBloccati] = useState<CodiciBloccati>(NESSUN_CODICE_BLOCCATO);
  const [risposte, setRisposte] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostraProblemi, setMostraProblemi] = useState(false);

  // All'apertura: la bozza dal documento e, se esiste già, quante risposte ha (codici bloccati).
  useEffect(() => {
    if (!open) return;
    setBozza(bozzaDaQuestionario(questionnaire));
    setScheda('generale');
    setError(null);
    setMostraProblemi(false);
    setBloccati(NESSUN_CODICE_BLOCCATO);
    setRisposte(null);
    if (!questionnaire) return;
    let annullato = false;
    contaRisposteQuestionario(questionnaire.id)
      .then((n) => {
        if (annullato) return;
        setRisposte(n);
        if (n > 0) setBloccati(codiciDelQuestionario(questionnaire));
      })
      .catch((e) => {
        console.error('[platform-crm] conteggio risposte del questionario fallito', e);
        // Senza il conteggio non si sa se ci sono risposte: per prudenza i codici si bloccano.
        if (!annullato) setBloccati(codiciDelQuestionario(questionnaire));
      });
    return () => { annullato = true; };
  }, [open, questionnaire]);

  const documento = useMemo(() => questionarioDaBozza(bozza), [bozza]);
  const problemi = useMemo(() => problemiDefinizione(documento), [documento]);

  const aggiorna = (patch: Partial<Bozza>) => { setBozza((b) => ({ ...b, ...patch })); setError(null); };

  const sposta = (indice: number, verso: -1 | 1) => setBozza((b) => {
    const arrivo = indice + verso;
    if (arrivo < 0 || arrivo >= b.domande.length) return b;
    const domande = [...b.domande];
    [domande[indice], domande[arrivo]] = [domande[arrivo], domande[indice]];
    return { ...b, domande };
  });

  const handleSave = async () => {
    if (problemi.length > 0) {
      setMostraProblemi(true);
      setError(`Ci sono ${problemi.length} cose da sistemare prima di salvare.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveQuestionnaire(documento, questionnaire);
      onSaved();
      onClose();
    } catch (e) {
      console.error('[platform-crm] salvataggio questionario fallito', e);
      setError(e instanceof Error ? e.message : 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const lingueSorgente = Object.keys(NOMI_LINGUA);

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontFamily: tokens.font.display, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', pb: 0 }}>
        {questionnaire ? 'Modifica questionario' : 'Nuovo questionario'}
      </DialogTitle>
      <Box sx={{ px: 3, borderBottom: `1px solid ${tokens.color.line}` }}>
        <Tabs value={scheda} onChange={(_, v) => setScheda(v)} variant="scrollable" sx={{ '& .MuiTab-root': { minHeight: 44, textTransform: 'none' } }}>
          <Tab value="generale" label="Generale" />
          <Tab value="domande" label={`Domande (${bozza.domande.length})`} />
          <Tab value="schermate" label="Schermate" />
          <Tab value="email" label="Email di conferma" />
          <Tab value="traduzioni" label="Traduzioni" />
        </Tabs>
      </Box>
      <DialogContent sx={{ minHeight: 420 }}>
        {risposte !== null && risposte > 0 && (scheda === 'domande') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Questo questionario ha già {risposte === 1 ? 'una risposta' : `${risposte} risposte`}: i codici di domande e opzioni
            esistenti non si cambiano più (si cambiano i testi). Le statistiche contano i codici.
          </Alert>
        )}

        {scheda === 'generale' && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <CampoTesto label="Titolo" value={bozza.titolo} onChange={(v) => aggiorna({ titolo: v })} autoFocus={!questionnaire} />
            <CampoTesto
              label="Descrizione"
              value={bozza.descrizione}
              onChange={(v) => aggiorna({ descrizione: v })}
              multiline
              minRows={2}
              helperText="Testo PUBBLICO: è l'introduzione quando manca la schermata di apertura. Mai note interne."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <FormControlLabel
                control={<Switch checked={bozza.attivo} onChange={(e) => aggiorna({ attivo: e.target.checked })} />}
                label={<Typography variant="body2">Attivo (compilabile dai link)</Typography>}
              />
              <TextField
                select
                size="small"
                label="Lingua dei testi"
                value={bozza.lingua_sorgente}
                onChange={(e) => aggiorna({ lingua_sorgente: e.target.value })}
                disabled={!!questionnaire}
                helperText={questionnaire ? 'Non si cambia: le altre lingue stanno nelle traduzioni' : 'Le altre lingue si aggiungono nelle traduzioni'}
                sx={{ minWidth: 220 }}
              >
                {(lingueSorgente.includes(bozza.lingua_sorgente) ? lingueSorgente : [...lingueSorgente, bozza.lingua_sorgente]).map((l) => (
                  <MenuItem key={l} value={l}>{NOMI_LINGUA[l] ?? l.toUpperCase()}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>
        )}

        {scheda === 'domande' && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {bozza.domande.map((d, i) => (
              <EditorDomanda
                key={d.chiave}
                domanda={d}
                indice={i}
                totale={bozza.domande.length}
                tutte={bozza.domande}
                codiceBloccato={bloccati.domande.has(d.id) && d.idFisso}
                opzioniBloccate={bloccati.opzioni.get(d.id) ?? new Set()}
                onChange={(nuova) => { setBozza((b) => applicaDomandaCambiata(b, d, nuova)); setError(null); }}
                onSu={() => sposta(i, -1)}
                onGiu={() => sposta(i, 1)}
                onRimuovi={() => aggiorna({ domande: bozza.domande.filter((x) => x.chiave !== d.chiave) })}
              />
            ))}
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => aggiorna({ domande: [...bozza.domande, nuovaDomanda(bozza.domande.map((x) => x.id))] })}
              sx={{ alignSelf: 'flex-start' }}
            >
              Aggiungi domanda
            </Button>
          </Stack>
        )}

        {scheda === 'schermate' && <SchedaSchermate bozza={bozza} onChange={aggiorna} />}
        {scheda === 'email' && <SchedaEmail bozza={bozza} onChange={aggiorna} />}
        {scheda === 'traduzioni' && <SchedaTraduzioni bozza={bozza} onChange={aggiorna} />}

        {mostraProblemi && problemi.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>Da sistemare prima di salvare</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {problemi.map((p, i) => <li key={i}><Typography variant="body2">{p.messaggio}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {problemi.length > 0 && !mostraProblemi && (
          <Button size="small" onClick={() => setMostraProblemi(true)} sx={{ mr: 'auto', color: tokens.color.warning }}>
            {problemi.length === 1 ? '1 cosa da sistemare' : `${problemi.length} cose da sistemare`}
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {saving ? 'Salvataggio...' : 'Salva questionario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
